import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RegularButton, Typography } from 'fronton-react';
import { InfoIcon, WarningCircleIcon } from '@fronton/icons-react';
import { GAClickButton, GAShow } from 'utils/analytics';
import { SelectedSchedule } from 'containers/instore/instore-service/components/texts';
import { selectPageModelTitle } from '../../../store/pageModel';
import { useEffectOnce } from '../../hooks';
import { InfoBar } from '../info-bar';
import {
	buttonStyles,
	infoBarStyles,
	selectedStoreDivStyles,
	StoreName,
	StyledDiv,
	Wrapper,
	StoreInfoWrapper,
} from './styles';

export const useGetOnClickHandler = ({ onClick, btnText }) => {
	const title = useSelector(selectPageModelTitle);

	const clickHandler = useCallback(() => {
		GAClickButton({
			elementText: btnText,
			elementName: title,
			elementBlock: 'servicePage',
		});

		onClick();
	}, [btnText, title, onClick]);

	return { clickHandler };
};

type StoreSelectionProps = {
	onClick: () => void;
};

export const StoreSelection = ({ onClick }: StoreSelectionProps) => {
	const btnText = 'Выбрать магазин';
	const { clickHandler } = useGetOnClickHandler({ btnText, onClick });

	return (
		<StyledDiv data-qa="store-selection">
			<Wrapper>
				<WarningCircleIcon type="fill" size="l" color="text-primary" />
				<Typography variant="m" size="body_accent">
					Выберите магазин, чтобы узнать стоимость и наличие услуг
				</Typography>
			</Wrapper>
			<RegularButton
				size="m"
				variant="accent"
				onClick={clickHandler}
				className={buttonStyles}
			>
				{btnText}
			</RegularButton>
		</StyledDiv>
	);
};

type SelectedStoreProps = {
	name: string;
	address: string | null;
	schedule?: string;
	link: string;
	onClick: () => void;
};

export const SelectedStore = ({ name, address, schedule, onClick, link }: SelectedStoreProps) => {
	const btnText = 'Изменить магазин';
	const { clickHandler } = useGetOnClickHandler({ btnText, onClick });

	const storeNameClickHandler = () => {
		GAClickButton({
			elementText: name,
			elementName: name,
			elementBlock: 'servicePage',
		});
	};

	return (
		<StyledDiv className={selectedStoreDivStyles} data-qa="selected-store">
			<StoreInfoWrapper>
				<StoreName
					href={`/shop/${link}/`}
					target="_blank"
					data-qa="store-name"
					onClick={storeNameClickHandler}
				>
					{name}
				</StoreName>
				{address && (
					<Typography variant="caption" data-qa="store-address">
						{address}
					</Typography>
				)}
				{schedule && <SelectedSchedule text={schedule} />}
			</StoreInfoWrapper>
			<RegularButton
				size="m"
				variant="accent"
				onClick={clickHandler}
				className={buttonStyles}
			>
				{btnText}
			</RegularButton>
		</StyledDiv>
	);
};

type StoreBlockProps = {
	storesCount: number;
	selectedStoreCode: number | null;
	selectedStoreData?: Omit<SelectedStoreProps, 'onClick'>;
	onClick: () => void;
};

export const StoreBlock = ({
	storesCount,
	selectedStoreCode,
	onClick,
	selectedStoreData,
}: StoreBlockProps) => {
	const title = useSelector(selectPageModelTitle);

	useEffectOnce(() => {
		if (!storesCount) {
			GAShow({
				elementBlock: 'servicePage',
				elementName: title,
				elementText: 'Магазины в регионе временно не работают',
			});
		}
	});

	if (storesCount === 0) {
		return (
			<InfoBar
				className={infoBarStyles}
				type="alert"
				icon={<InfoIcon type="outline" size="l" color="text-primary" />}
				title="Магазины в регионе временно не работают"
				paragraph="Выберите другой регион для того, чтобы узнать стоимость и наличие услуг"
			/>
		);
	}

	if (selectedStoreCode) {
		return <SelectedStore {...selectedStoreData} onClick={onClick} />;
	}

	return <StoreSelection onClick={onClick} />;
};
